import { motion, AnimatePresence } from "framer-motion";
import { ArrowDown, MapPin, Volume2, VolumeX, Play, Pause } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import heroImage from "@/assets/hero-villa.jpg";

const brandPillars = [
  { 
    text: "Exclusivity", 
    subtitle: "Rare properties for the discerning few" 
  },
  { 
    text: "Expertise", 
    subtitle: "The counsel of quiet understanding" 
  },
  { 
    text: "Legacy", 
    subtitle: "Investments measured in generations" 
  },
];

// Premium video backgrounds - using reliable Pexels CDN sources
const HERO_VIDEOS = [
  {
    url: "https://videos.pexels.com/video-files/3571264/3571264-uhd_2560_1440_30fps.mp4",
    location: "Monaco"
  },
  {
    url: "https://videos.pexels.com/video-files/3015510/3015510-hd_1920_1080_24fps.mp4",
    location: "Mediterranean"
  },
  {
    url: "https://videos.pexels.com/video-files/3214472/3214472-uhd_2560_1440_25fps.mp4",
    location: "Italian Coast"
  }
];

export const HeroSection = () => {
  const [currentPillar, setCurrentPillar] = useState(0);
  const [currentVideo, setCurrentVideo] = useState(0);
  const [isMuted, setIsMuted] = useState(true);
  const [isPlaying, setIsPlaying] = useState(true);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [useVideo, setUseVideo] = useState(true);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentPillar((prev) => (prev + 1) % brandPillars.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Video Loading Shimmer */}
      {useVideo && !videoLoaded && (
        <div className="absolute inset-0 bg-charcoal z-[1]">
          <div className="absolute inset-0 overflow-hidden">
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-transparent via-cream/5 to-transparent"
              animate={{
                x: ["-100%", "100%"],
              }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
                ease: "linear",
              }}
            />
          </div>
          <div className="absolute inset-0 flex items-center justify-center">
            <motion.div
              className="flex flex-col items-center gap-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              <motion.div
                className="w-12 h-12 border-2 border-gold/30 border-t-gold rounded-full"
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              />
              <span className="font-body text-xs uppercase tracking-[0.3em] text-cream/50">
                Loading Experience
              </span>
            </motion.div>
          </div>
        </div>
      )}

      {/* Video Background */}
      {useVideo && (
        <div className="absolute inset-0">
          <video
            ref={videoRef}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            onCanPlayThrough={() => setVideoLoaded(true)}
            onEnded={() => {
              setCurrentVideo((prev) => (prev + 1) % HERO_VIDEOS.length);
              setVideoLoaded(false);
            }}
            className={`w-full h-full object-cover transition-opacity duration-1000 ${
              videoLoaded ? "opacity-100" : "opacity-0"
            }`}
          >
            <source src={HERO_VIDEOS[currentVideo].url} type="video/mp4" />
          </video>
          {/* Gradient overlays for readability */}
          <div className="absolute inset-0 bg-gradient-to-b from-charcoal/40 via-charcoal/20 to-charcoal/60" />
          <div className="absolute inset-0 bg-charcoal/30" />
        </div>
      )}

      {/* Fallback Image Background (shown while video loads or if video disabled) */}
      <motion.div 
        className={`absolute inset-0 transition-opacity duration-1000 ${
          useVideo && videoLoaded ? "opacity-0" : "opacity-100"
        }`}
        initial={{ scale: 1.1 }}
        animate={{ scale: 1 }}
        transition={{ duration: 20, ease: "linear" }}
      >
        <img
          src={heroImage}
          alt="Luxury Mediterranean villa with infinity pool overlooking the sea at sunset"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-hero" />
        <div className="absolute inset-0 bg-charcoal/20" />
      </motion.div>

      {/* Video Controls */}
      {useVideo && videoLoaded && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2 }}
          className="absolute bottom-10 right-8 z-20 flex items-center gap-2"
        >
          <button
            onClick={togglePlay}
            className="p-2 rounded-full bg-charcoal/40 backdrop-blur-sm border border-cream/20 text-cream/70 hover:text-cream hover:border-cream/40 transition-all duration-300"
            aria-label={isPlaying ? "Pause video" : "Play video"}
          >
            {isPlaying ? <Pause size={16} /> : <Play size={16} />}
          </button>
          <button
            onClick={toggleMute}
            className="p-2 rounded-full bg-charcoal/40 backdrop-blur-sm border border-cream/20 text-cream/70 hover:text-cream hover:border-cream/40 transition-all duration-300"
            aria-label={isMuted ? "Unmute video" : "Mute video"}
          >
            {isMuted ? <VolumeX size={16} /> : <Volume2 size={16} />}
          </button>
        </motion.div>
      )}

      {/* Decorative Elements */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Corner accents */}
        <div className="absolute top-8 left-8 w-24 h-24 border-l border-t border-cream/20" />
        <div className="absolute top-8 right-8 w-24 h-24 border-r border-t border-cream/20" />
        <div className="absolute bottom-32 left-8 w-24 h-24 border-l border-b border-cream/20" />
        <div className="absolute bottom-32 right-8 w-24 h-24 border-r border-b border-cream/20" />
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-6 lg:px-12 text-center">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.3 }}
          className="max-w-5xl mx-auto"
        >
          {/* Animated Pillar Badge */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="mb-10"
          >
            <div className="inline-flex items-center gap-3 px-6 py-3 border border-cream/30 bg-charcoal/30 backdrop-blur-sm">
              <span className="w-2 h-2 rounded-full bg-gold animate-pulse" />
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentPillar}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.5 }}
                  className="flex flex-col items-start"
                >
                  <span className="font-body text-xs uppercase tracking-[0.3em] text-gold">
                    {brandPillars[currentPillar].text}
                  </span>
                  <span className="font-body text-[10px] text-cream/60 tracking-wide">
                    {brandPillars[currentPillar].subtitle}
                  </span>
                </motion.div>
              </AnimatePresence>
            </div>
          </motion.div>

          {/* Main Headline */}
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-light text-cream leading-[1.05] mb-4">
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="block"
            >
              Noble Legacy.
            </motion.span>
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="block font-medium italic mt-2 text-gold-light"
            >
              Global Destiny.
            </motion.span>
          </h1>

          {/* Refined Description */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="font-body text-lg md:text-xl text-cream/80 max-w-2xl mx-auto mb-12 leading-relaxed"
          >
            Where provenance meets vision. Curating exceptional estates 
            for families who measure wealth in generations.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.2 }}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          >
            <Button variant="hero-solid" size="xl" asChild className="group">
              <Link to="/properties" className="flex items-center gap-2">
                <span>View Collection</span>
                <motion.span
                  className="inline-block"
                  whileHover={{ x: 5 }}
                  transition={{ duration: 0.2 }}
                >
                  →
                </motion.span>
              </Link>
            </Button>
            <Button variant="hero" size="xl" asChild>
              <a href="#contact">Schedule Consultation</a>
            </Button>
          </motion.div>

          {/* Location Badge with Separator Lines */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 1.4 }}
            className="flex items-center justify-center gap-4 mt-16"
          >
            <div className="h-px w-12 bg-gradient-to-r from-transparent to-cream/40" />
            <div className="flex items-center gap-2 text-cream/70">
              <MapPin size={14} className="text-gold" />
              <span className="font-body text-sm tracking-widest uppercase">
                Italy · Spain · Monaco · Dubai
              </span>
            </div>
            <div className="h-px w-12 bg-gradient-to-l from-transparent to-cream/40" />
          </motion.div>

          {/* Pillar Indicators */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 1.6 }}
            className="flex items-center justify-center gap-2 mt-8"
          >
            {brandPillars.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentPillar(index)}
                className={`w-8 h-1 rounded-full transition-all duration-500 ${
                  index === currentPillar 
                    ? "bg-gold w-12" 
                    : "bg-cream/30 hover:bg-cream/50"
                }`}
                aria-label={`View ${brandPillars[index].text} pillar`}
              />
            ))}
          </motion.div>
        </motion.div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, delay: 1.8 }}
        className="absolute bottom-10 left-1/2 -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="flex flex-col items-center text-cream/50"
        >
          <span className="font-body text-xs uppercase tracking-[0.2em] mb-2">
            Discover
          </span>
          <ArrowDown size={20} />
        </motion.div>
      </motion.div>
    </section>
  );
};
